import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, CreditCard, Clock, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConsultationCheckout() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [price, setPrice] = useState(75);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch consultation price
    axios.get(`${API}/api/consultation/price`)
      .then(res => setPrice(res.data.price))
      .catch(err => console.error("Failed to fetch price:", err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!name.trim() || !email.trim()) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/consultation/checkout`, {
        origin_url: window.location.origin,
        name: name.trim(),
        email: email.trim()
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError("Une erreur est survenue. Veuillez réessayer.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F1EB] to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#E8E4DE] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#2D2D2D] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#2F4F4F]/10 text-[#2F4F4F] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Calendar className="w-4 h-4" />
            Consultation personnalisée
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#2D2D2D] mb-3">
            Réservez votre consultation
          </h1>
          <p className="text-[#5C5C5C] text-lg">
            30 minutes d'échange avec Pierre Saint-Hubert
          </p>
        </div>

        {/* Pricing Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-[#E8E4DE] overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-[#2F4F4F] to-[#3B7A8C] p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Consultation 30 min</h2>
                <p className="text-white/80 mt-1">Accompagnement personnalisé</p>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold">{price}€</span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E2725B]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#E2725B]" />
                </div>
                <div>
                  <p className="font-medium text-[#2D2D2D]">30 minutes</p>
                  <p className="text-sm text-[#8A8A8A]">En visioconférence</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#2F4F4F]/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-[#2F4F4F]" />
                </div>
                <div>
                  <p className="font-medium text-[#2D2D2D]">Choix du créneau</p>
                  <p className="text-sm text-[#8A8A8A]">Après paiement</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">
                  Votre nom complet
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jean Dupont"
                  className="w-full"
                  data-testid="consultation-name-input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">
                  Votre email
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jean@example.com"
                  className="w-full"
                  data-testid="consultation-email-input"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#E2725B] to-[#D4A574] hover:opacity-90 text-white py-6 rounded-full text-lg font-medium"
                data-testid="pay-consultation-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Redirection...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payer {price}€ et réserver
                  </>
                )}
              </Button>

              <p className="text-center text-xs text-[#8A8A8A]">
                Paiement sécurisé par Stripe. Après le paiement, vous pourrez choisir votre créneau.
              </p>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
